import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { useRecordTypes } from 'views/RecordType/hooks/useRecordTypes';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import FlexSelect from 'components/Inputs/FlexSelect';
import { Badge } from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { useHistory, useParams } from 'react-router-dom';
import Service from '../service';

import { toast } from 'react-toastify';

import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody
} from 'reactstrap';
import ReactLoader from 'components/Loader';
import ReactButton from 'components/Buttons/ReactButton';
import { ScreenPrompt } from 'components/ScreenPrompt';
import { DragAndDrop } from 'components/DragAndDrop';
import { FlexMediaPreviewer } from 'components/FlexMediaPreviewer';

const accountStandardFields = [
  { key: 'name', label: 'Nome' },
  { key: 'phone', label: 'Telefone' },
  { key: 'customerPortfolioId', label: 'Categoria dos Clientes' },
  { key: 'responsible', label: 'Responsável' }
];

const contactStandardFields = [
  { key: 'name', label: 'Nome' },
  { key: 'phone', label: 'Telefone' },
  { key: 'email', label: 'E-mail' }
];

const ALLOWED_EXTENSIONS_BY_MIME_TYPE = {
  IMAGE: '.png, .jpg, .jpeg, .gif, .webp, .bmp, .heif, .heic'
};

function getEntityVariables(customFields, standardFields, entityPrefix) {
  const variables = {
    standard: [],
    custom: []
  };

  if (standardFields) {
    variables.standard = standardFields.map((field) => ({
      key: `{{${entityPrefix}.${field.key}}}`,
      name: field.label
    }));
  }

  if (Array.isArray(customFields)) {
    variables.custom = customFields
      .filter((field) => !field.recordTypesConfig?.isOnlyToShowOnEdit)
      .map((field) => ({
        key: `{{${entityPrefix}.${field.name}}}`,
        name: field.label || field.name
      }));
  }

  return variables;
}

const ProposalForm = () => {
  const history = useHistory();
  const { id } = useParams();
  const [loadingFetch, setLoadingFetch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const { control, handleSubmit, errors, formState, reset, setValue, watch } =
    useForm();

  const { recordTypes } = useRecordTypes('');
  const [recordTypesAccount, setRecordTypesAccount] = useState([]);
  const [recordTypesContact, setRecordTypesContact] = useState([]);
  const selectedAccountRecordTypeId = watch('account_record_type');
  const selectedContactRecordTypeId = watch('contact_record_type');
  const informationsClientRef = useRef();

  useEffect(() => {
    if (recordTypes && recordTypes.length > 0) {
      const accountRecordTypes = recordTypes
        .filter((rt) => rt.entity === entitiesName.ACCOUNT)
        .map((rt) => ({
          value: rt.id,
          label: rt.name
        }));
      const contactRecordTypes = recordTypes
        .filter((rt) => rt.entity === entitiesName.CONTACT)
        .map((rt) => ({
          value: rt.id,
          label: rt.name
        }));
      setRecordTypesAccount(accountRecordTypes);
      setRecordTypesContact(contactRecordTypes);
    }
  }, [recordTypes]);

  const {
    customFields: accountCustomFields,
    getCustomFields: getAccountCustomFields,
    isLoadingCustomFields: isLoadingAccountCustomFields
  } = useEntityCustomFieldsByRecordType('ACCOUNT', selectedAccountRecordTypeId);
  const {
    customFields: contactCustomFields,
    getCustomFields: getContactCustomFields,
    isLoadingCustomFields: isLoadingContactCustomFields
  } = useEntityCustomFieldsByRecordType('CONTACT', selectedContactRecordTypeId);

  useEffect(() => {
    if (selectedAccountRecordTypeId) getAccountCustomFields();
  }, [selectedAccountRecordTypeId, getAccountCustomFields]);
  useEffect(() => {
    if (selectedContactRecordTypeId) getContactCustomFields();
  }, [selectedContactRecordTypeId, getContactCustomFields]);

  const availableVariables = useMemo(() => {
    const accountVars = getEntityVariables(
      accountCustomFields,
      accountStandardFields,
      'account'
    );
    const contactVars = getEntityVariables(
      contactCustomFields,
      contactStandardFields,
      'contact'
    );

    const sections = [];

    if (accountVars.standard.length > 0) {
      sections.push({
        title: 'Conta - Campos Padrão',
        variables: accountVars.standard
      });
    }
    if (accountVars.custom.length > 0) {
      sections.push({
        title: 'Conta - Campos Personalizados',
        variables: accountVars.custom
      });
    }
    if (contactVars.standard.length > 0) {
      sections.push({
        title: 'Contato - Campos Padrão',
        variables: contactVars.standard
      });
    }
    if (contactVars.custom.length > 0) {
      sections.push({
        title: 'Contato - Campos Personalizados',
        variables: contactVars.custom
      });
    }
    return sections;
  }, [accountCustomFields, contactCustomFields]);

  const insertVariableAtCursor = (variable) => {
    const textarea = informationsClientRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;
      const newValue =
        value.substring(0, start) + variable + value.substring(end);
      textarea.value = newValue;
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + variable.length;
      setValue('informationsClient', newValue, { shouldDirty: true });
    }
  };

  useEffect(() => {
    if (id) {
      setLoadingFetch(true);
      Service.getProposalById(id)
        .then(async (res) => {
          const data = res.data.data || res.data;
          reset(data);
          setIsUpdate(true);

          try {
            const logoRes = await Service.getProposalLogo(id);
            const blob = logoRes?.data;
            if (blob && blob.size > 0) {
              const file = new File([blob], 'logo', {
                type: blob.type || 'image/png'
              });
              setPreviewFile(file);
            }
          } catch (err) {
            // Logo não encontrada, ignora o erro
          }
        })
        .catch(() => {
          toast.error('Erro ao tentar carregar proposta', {
            autoClose: 3000,
            closeOnClick: true
          });
        })
        .finally(() => setLoadingFetch(false));
    }
  }, [id, reset]);

  const { isDirty } = formState;

  const onSubmit = async (data, e) => {
    e.preventDefault();
    const dataDTO = {
      name: data.name,
      title: data.title,
      informationsClient: data.informationsClient,
      informations: data.informations,
      validity: data.validity,
      active: data.active !== undefined ? data.active : true
    };

    try {
      setSaving(true);

      if (isUpdate) {
        await Service.putProposalById(id, dataDTO);
        if (selectedFile) {
          const formData = new FormData();
          formData.append('proposalId', id);
          formData.append('file', selectedFile);
          await Service.updateProposalLogo(formData);
        }
        toast.success('Proposta atualizada com sucesso!', {
          autoClose: 3000,
          closeOnClick: true
        });
      } else {
        const response = await Service.createProposal(dataDTO);
        const newProposalId = response.data.data?.id || response.data.id;

        if (selectedFile && newProposalId) {
          const formData = new FormData();
          formData.append('proposalId', newProposalId);
          formData.append('file', selectedFile);
          await Service.updateProposalLogo(formData);
        }
        toast.success('Proposta criada com sucesso!', {
          autoClose: 3000,
          closeOnClick: true
        });
      }

      history.push('/admin/crm/proposal');
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          `Ocorreu um erro ao ${isUpdate ? 'atualizar' : 'cadastrar'} proposta`,
        {
          autoClose: 3000,
          closeOnClick: true
        }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e?.[0] ?? e?.target?.files?.[0];
    if (!file)
      return toast.error('Não foi possível selecionar o arquivo', {
        autoClose: 3000,
        closeOnClick: true
      });
    setSelectedFile(file);
    setPreviewFile(file);
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/crm/proposal');
  };

  return (
    <>
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message="Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar"
      />
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/crm/proposal"
        buttonTitle="Voltar para a listagem"
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <h3 className="mb-0">Proposta</h3>
                </CardHeader>
                <CardBody>
                  {loadingFetch ? (
                    <Row className="justify-content-md-center">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">Nome*</label>
                          <Controller
                            as={Input}
                            control={control}
                            name="name"
                            defaultValue=""
                            rules={{
                              required: 'Nome é um campo obrigatório'
                            }}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="name"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">Título*</label>
                          <Controller
                            as={Input}
                            control={control}
                            name="title"
                            defaultValue=""
                            rules={{
                              required: 'Título é um campo obrigatório'
                            }}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="title"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">
                            Tipo de Registro da Conta
                          </label>
                          <Controller
                            name="account_record_type"
                            control={control}
                            defaultValue={recordTypesAccount[0]?.value || ''}
                            render={(props) => (
                              <FlexSelect
                                isClearable={true}
                                dataOptions={recordTypesAccount}
                                isMulti={false}
                                closeMenuOnSelect={true}
                                value={props.value}
                                valueController={(fieldName, value) =>
                                  setValue(fieldName, value)
                                }
                                fieldName="account_record_type"
                                labelName="label"
                                valueName="value"
                              />
                            )}
                          />
                          <label className="form-control-label mt-2">
                            Tipo de Registro do Contato
                          </label>
                          <Controller
                            name="contact_record_type"
                            control={control}
                            defaultValue={recordTypesContact[0]?.value || ''}
                            render={(props) => (
                              <FlexSelect
                                isClearable={true}
                                dataOptions={recordTypesContact}
                                isMulti={false}
                                closeMenuOnSelect={true}
                                value={props.value}
                                valueController={(fieldName, value) =>
                                  setValue(fieldName, value)
                                }
                                fieldName="contact_record_type"
                                labelName="label"
                                valueName="value"
                              />
                            )}
                          />
                          <label className="form-control-label mt-2">
                            Informações do Cliente
                          </label>
                          <Controller
                            name="informationsClient"
                            control={control}
                            defaultValue=""
                            render={(props) => (
                              <Input
                                type="textarea"
                                rows="4"
                                innerRef={informationsClientRef}
                                value={props.value}
                                onChange={props.onChange}
                              />
                            )}
                          />
                          <div className="mt-2">
                            <label className="form-control-label">
                              Variáveis disponíveis:
                            </label>
                            {isLoadingAccountCustomFields ||
                            isLoadingContactCustomFields ? (
                              <p className="small text-muted">
                                Carregando campos...
                              </p>
                            ) : (
                              availableVariables.length > 0 &&
                              availableVariables.map((section) => (
                                <div key={section.title} className="mb-3">
                                  <h5
                                    className="text-muted"
                                    style={{
                                      fontSize: '0.85rem',
                                      fontWeight: 600,
                                      marginTop: '0.5rem',
                                      marginBottom: '0.5rem'
                                    }}
                                  >
                                    {section.title}
                                  </h5>
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: 6,
                                      flexWrap: 'wrap'
                                    }}
                                  >
                                    {section.variables.map((variable) => (
                                      <Badge
                                        key={variable.key}
                                        pill={false}
                                        color="primary"
                                        style={{
                                          textTransform: 'none',
                                          cursor: 'pointer',
                                          fontSize: '0.8rem',
                                          fontWeight: 500
                                        }}
                                        onClick={() =>
                                          insertVariableAtCursor(variable.key)
                                        }
                                        title={`Inserir ${variable.name}`}
                                      >
                                        {variable.name}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">
                            Informações da Proposta
                          </label>
                          <Controller
                            as={Input}
                            type="textarea"
                            rows="4"
                            control={control}
                            name="informations"
                            defaultValue=""
                          />

                          <Row className="mb-3">
                            <Col sm="6">
                              <label className="form-control-label">
                                Logo da Proposta
                              </label>
                              <DragAndDrop
                                acceptedFileExtensions={
                                  ALLOWED_EXTENSIONS_BY_MIME_TYPE.IMAGE
                                }
                                name="update_media_file"
                                id="update_media_file"
                                changeFile={handleFileChange}
                                description="Selecione a logo da proposta (imagem)"
                              />
                            </Col>
                            <Col sm="6">
                              {previewFile && (
                                <>
                                  <label className="form-control-label">
                                    Preview
                                  </label>
                                  <FlexMediaPreviewer file={previewFile} />
                                </>
                              )}
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">
                            Validade (em dias)
                          </label>
                          <div className="d-flex align-items-center">
                            <Controller
                              control={control}
                              name="validity"
                              defaultValue={0}
                              rules={{
                                validate: (value) => {
                                  const num = parseInt(value, 10);
                                  if (isNaN(num) || num < 0) {
                                    return 'Validade deve ser um número maior ou igual a 0';
                                  }
                                  return true;
                                }
                              }}
                              render={(props) => (
                                <Input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={props.value}
                                  onChange={props.onChange}
                                  placeholder="0"
                                  style={{ maxWidth: '150px' }}
                                />
                              )}
                            />
                            <span className="ml-2">dias</span>
                          </div>
                          <ErrorMessage
                            errors={errors}
                            name="validity"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>
                      <hr />
                      <ReactButton
                        btnColor="confirmation"
                        type="submit"
                        disabled={saving}
                      >
                        Salvar
                      </ReactButton>
                      <ReactButton
                        btnColor="cancelation"
                        onClick={() => cancelAction()}
                        disabled={saving}
                      >
                        Cancelar
                      </ReactButton>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default ProposalForm;
